'use client';

import { useTranslation } from '@/hooks/useTranslation';
import Image from 'next/image';
import { FiClock } from 'react-icons/fi';
import { BiSolidCheckCircle } from 'react-icons/bi';
import { HiOutlineDocumentText, HiOutlineLightBulb } from 'react-icons/hi';
import { HiOutlineSparkles } from 'react-icons/hi2';
import CTABanner from '@/components/blocks/CTABanner';
import { AnimatedGradientWrapper } from '@/components/background/AnimatedGradientWrapper';

interface PageContentProps {
  locale: string;
}

export default function PageContent({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  const features = [
    {
      icon: HiOutlineLightBulb,
      title: t('LeadForm/Sections/WhyDropdownsMatter/EaseOfUse/Title'),
      description: t('LeadForm/Sections/WhyDropdownsMatter/EaseOfUse/Description'),
    },
    {
      icon: HiOutlineDocumentText,
      title: t('LeadForm/Sections/WhyDropdownsMatter/ConsistentData/Title'),
      description: t('LeadForm/Sections/WhyDropdownsMatter/ConsistentData/Description'),
    },
    {
      icon: FiClock,
      title: t('LeadForm/Sections/WhyDropdownsMatter/TimeEfficiency/Title'),
      description: t('LeadForm/Sections/WhyDropdownsMatter/TimeEfficiency/Description'),
    },
  ];

  const integrationCases = [
    {
      icon: HiOutlineSparkles,
      title: t('LeadForm/Sections/SeamlessIntegrations/case1'),
      description: t('LeadForm/Sections/SeamlessIntegrations/case11'),
    },
    {
      icon: BiSolidCheckCircle,
      title: t('LeadForm/Sections/SeamlessIntegrations/case2'),
      description: t('LeadForm/Sections/SeamlessIntegrations/case22'),
    },
    {
      icon: HiOutlineDocumentText,
      title: t('LeadForm/Sections/SeamlessIntegrations/case3'),
      description: t('LeadForm/Sections/SeamlessIntegrations/case33'),
    },
  ];

  return (
    <AnimatedGradientWrapper>
      <div className="tw-mb-4">
        <section className="tw-relative tw-pt-10 lg:tw-pt-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
          <div className="tw-container tw-mx-auto  tw-max-w-7xl">
            {/* Header */}
            <div className="tw-text-center tw-mb-16">
              <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
                <HiOutlineSparkles className="tw-w-4 tw-h-4" />
                {t('LeadForm/Page/Article')}
              </div>

              <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-font-['Lexend']">
                {t('LeadForm/Page/BoostLeadForms')}
              </h1>
              <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-4xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-font-['Lexend']">
                {t('LeadForm/Page/BoostLeadForms2')}
              </h2>

              <p className="tw-text-lg tw-text-gray-600 tw-max-w-2xl tw-mx-auto tw-font-['Lexend']">
                {t('LeadForm/Page/Intro')}
              </p>
            </div>

            {/* Main Image */}
            <div className="tw-flex tw-justify-center tw-items-center tw-w-full tw-mb-16">
              <div className="tw-w-[600px] tw-h-[400px] tw-rounded-3xl tw-overflow-hidden tw-shadow-2xl tw-group tw-transition-all tw-duration-300 hover:tw-shadow-3xl">
                <Image
                  priority
                  quality={100}
                  src="https://cdn.1cdn.app/application/LOUISEBOT/25011116585345_dropdown_louisebot.png"
                  alt="LouiseBot Dropdown Forms"
                  width={600}
                  height={400}
                  style={{
                    width: '600px',
                    height: '400px',
                    objectFit: 'cover',
                  }}
                  className="tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
                />
              </div>
            </div>

            {/* Why Dropdowns Matter */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('LeadForm/Sections/WhyDropdownsMatter/Title')}
              </h2>

              <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group"
                  >
                    <div className="tw-flex tw-items-center tw-mb-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-blue-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-4">
                        <feature.icon className="tw-w-8 tw-h-8 tw-text-blue-600" />
                      </div>
                    </div>
                    <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend']">
                      {feature.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend']">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Seamless Integrations */}
            <div className="tw-mb-16 lg:tw-mb-24">
              <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-8 tw-font-['Lexend'] tw-text-center">
                {t('LeadForm/Sections/SeamlessIntegrations/Title')}
              </h2>

              <div className="tw-grid md:tw-grid-cols-3 tw-gap-8">
                {integrationCases.map((integration, index) => (
                  <div
                    key={index}
                    className="tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-200 tw-shadow-lg tw-transition-all tw-duration-300 hover:tw-shadow-xl hover:tw-scale-105 tw-group"
                  >
                    <div className="tw-flex tw-items-center tw-mb-4">
                      <div className="tw-w-16 tw-h-16 tw-bg-purple-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mr-4">
                        <integration.icon className="tw-w-8 tw-h-8 tw-text-purple-600" />
                      </div>
                    </div>
                    <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-3 tw-font-['Lexend']">
                      {integration.title}
                    </h3>
                    <p className="tw-text-gray-600 tw-font-['Lexend']">{integration.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <section className="tw-pt-10 lg:tw-pt-10 ">
              <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-4xl">
                <div className="tw-text-center tw-mb-16">
                  <h1 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-font-['Lexend']">
                    {t('LeadForm/Sections/BoostProductivity/Title')}
                  </h1>
                  <p className="tw-text-lg tw-text-gray-600 tw-max-w-2xl tw-mx-auto tw-font-['Lexend']">
                    {t('LeadForm/Sections/BoostProductivity/Intro')}
                  </p>
                </div>
              </div>
            </section>
          </div>
        </section>
        {/* Final CTA */}
        <CTABanner
          cta={t('LeadForm/Sections/BoostProductivity/BoostNow')}
          ctaTarget="register"
          title={t('LeadForm/Sections/BoostProductivity/CallToAction')}
          locale={locale}
        />
      </div>
    </AnimatedGradientWrapper>
  );
}
